import { AfterViewInit, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { CvdChartData, CvdChartDataValue } from '@covance/ng-cvd3';
import { chain, find } from 'lodash';
import { VisitInfo } from './lab-trends-chart.component';
import { LabTestResult } from '../../lab-test-results';
import { removeAllTooltips } from '../../core/tooltip-remover';
import { canBeUsedInTrends } from '../../charts/chart-helper';
import { SiteTestDataStorage } from './site-test-data-storage';

export class TestAxisTitleInfo {
    public zcTestCode: string;
    public testName: string;
    public unit: string;
    public testNameWithUnit: string;

    constructor(labTestResult: LabTestResult) {
        this.zcTestCode = labTestResult.zcTestCode;
        this.testName = labTestResult.test;
        this.unit = labTestResult.unit;
        this.testNameWithUnit = labTestResult.testNameWithUnit;
    }

    public get axisTitle() {
        return this.testNameWithUnit;
    }

    public toString(): string {
        return this.axisTitle;
    }
}

export abstract class BaseOsrChart implements OnDestroy, AfterViewInit {

    public lineChartData: CvdChartData<CvdChartDataValue>[];
    public yAxisNames: TestAxisTitleInfo[];
    public yAxisName: TestAxisTitleInfo;

    public get yAxisTitle() {
        return this.yAxisName && this.yAxisName.axisTitle;
    }

    protected data: LabTestResult[] = [];
    protected currentTestData: LabTestResult[] = [];
    protected visitsInfo: VisitInfo[];
    private subscription: Subscription;
    private isViewInitialized: boolean;

    protected constructor(private storage: SiteTestDataStorage, private changeDetectorRef: ChangeDetectorRef) {
    }

    public ngOnChanges() {
        if (this.isViewInitialized) {
            this.build();
        }
    }

    public ngAfterViewInit() {
        this.subscription = this.storage.getData.subscribe((data) => {
            if (!data) {
                return;
            }
            this.data = data.filter((r) => canBeUsedInTrends(r));
            this.build();
            this.changeDetectorRef.markForCheck();
        });
    }

    public ngOnDestroy() {
        removeAllTooltips();
        this.subscription.unsubscribe();
    }

    public onChangeYAxisName(yAxisName: TestAxisTitleInfo[]) {
        if (!yAxisName) {
            return;
        }
        this.yAxisName = yAxisName[0];
        this.currentTestData = this.data.filter(r => r.zcTestCode === this.yAxisName.zcTestCode);
        this.lineChartData = this.buildChartData();
        this.chartSpecific();
    }

    protected abstract buildChartData(): CvdChartData<CvdChartDataValue>[];

    protected abstract chartSpecific(): void;

    private buildVisitsInfo() {
        this.visitsInfo = chain(this.data)
            .uniqBy(res => res.visitCode)
            .map(res => ({
                visitCode: res.visitCode,
                visitTitle: res.visitName
            } as VisitInfo)).value();
    }

    private build() {
        this.buildVisitsInfo();
        this.buildAxisNames();
        this.currentTestData = this.data.filter(r => r.zcTestCode === this.yAxisName.zcTestCode);
        this.lineChartData = this.buildChartData();
        this.chartSpecific();
    }

    private buildAxisNames() {
        this.yAxisNames = chain(this.data)
            .map(r => new TestAxisTitleInfo(r))
            .uniqBy(ax => ax.zcTestCode)
            .orderBy(ax => ax.testName.toLowerCase())
            .value();
        this.yAxisName = find(this.yAxisNames, yName => this.yAxisName && this.yAxisName.zcTestCode === yName.zcTestCode) || this.yAxisNames[0];
    }
}
